import { ArrowLeft, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

interface EntityDetailHeaderProps {
  /** Called when the back arrow is clicked. */
  onBack: () => void;
  /** Leading icon rendered in the brand-gradient tile. */
  icon: LucideIcon;
  /** Primary title (truncates on overflow). */
  name: string;
  /** Optional secondary line under the title (truncates on overflow). */
  description?: string | null;
  /** Optional badge rendered next to the name (e.g. an "Everyone" pill). */
  badge?: ReactNode;
  /** Right-aligned action buttons. */
  actions?: ReactNode;
  /** Content rendered below the header row (e.g. a stats bar). */
  children?: ReactNode;
}

/**
 * Shared detail-page header: back arrow + icon + name/description + actions.
 *
 * Layout is a single flex row with three regions:
 *   [back]  [icon + name/description (flex-1, min-w-0)]  [actions (shrink-0)]
 *
 * The middle region shrinks and truncates so long names/descriptions never
 * crowd or push out the action buttons on narrow widths.
 */
export function EntityDetailHeader({
  onBack,
  icon: Icon,
  name,
  description,
  badge,
  actions,
  children,
}: Readonly<EntityDetailHeaderProps>) {
  return (
    <div data-testid="entity-detail-header" className="shrink-0 p-6 border-b border-border-primary">
      <div className="flex items-center gap-4 mb-4">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="p-2 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div
            data-testid="entity-detail-header-icon"
            className="h-12 w-12 rounded-xl bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center shrink-0"
          >
            <Icon className="h-6 w-6 text-brand-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h1
                data-testid="entity-detail-header-name"
                className="text-2xl font-semibold text-content-primary truncate"
              >
                {name}
              </h1>
              {badge}
            </div>
            {description && (
              <p data-testid="entity-detail-header-description" className="text-content-tertiary mt-0.5 truncate">
                {description}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div data-testid="entity-detail-header-actions" className="flex items-center gap-2 shrink-0">
            {actions}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}
